import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Search, Play, CheckCircle2, AlertTriangle } from 'lucide-react';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * Entity merge panel: Preview/Apply for retiring a duplicate entity
 * and reassigning all its references to the survivor.
 */
export function EntityMergePanel() {
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    setPreview(null);
    setApplyResult(null);
    try {
      const { data } = await diagnosticsAPI.entityMergePreview();
      setPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    try {
      const { data } = await diagnosticsAPI.entityMergeApply();
      setApplyResult(data);
      setPreview(null);
      toast.success(`Merge applied: ${data.total_modified} records reassigned`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const affectedResults = preview?.scan_results?.filter(r => r.count > 0) || [];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Entity Merge: Retire Duplicate</CardTitle>
        <p className="text-xs text-muted-foreground">
          Scan all collections for references to the retired entity (fz_pk) and
          reassign them to the survivor (fan_zhang_piotr_kacki). Two-step Preview/Apply.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Controls */}
        <div className="flex gap-2">
          <Button onClick={runPreview} disabled={loading || applying} variant="outline" size="sm">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
            Preview Merge
          </Button>
          {preview && preview.total_references > 0 && (
            <Button onClick={runApply} disabled={applying} size="sm" variant="destructive">
              {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
              Apply Merge ({preview.total_references} references)
            </Button>
          )}
        </div>

        {/* Preview results */}
        {preview && (
          <div className="space-y-3">
            <div className="flex gap-4 text-sm">
              <span>
                Retire: <Badge variant="outline" className="text-red-600 border-red-300">{preview.retire_entity?.canonical_name}</Badge>
              </span>
              <span>
                Survivor: <Badge variant="outline" className="text-green-600 border-green-300">{preview.survivor_entity?.canonical_name}</Badge>
              </span>
            </div>

            <div className="text-sm">
              Total references: <strong>{preview.total_references}</strong> across{' '}
              <strong>{preview.affected_collections}</strong> collection/field pairs
            </div>

            {preview.total_references === 0 && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="h-4 w-4" /> No references to retired entity found. Already merged or never used.
              </div>
            )}

            {affectedResults.length > 0 && (
              <div className="border rounded overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">Collection</th>
                      <th className="text-left px-2 py-1.5 font-medium">Field</th>
                      <th className="text-left px-2 py-1.5 font-medium">Match Type</th>
                      <th className="text-right px-2 py-1.5 font-medium">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affectedResults.map((r, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-muted/20">
                        <td className="px-2 py-1.5">{r.collection}</td>
                        <td className="px-2 py-1.5 font-mono">{r.field}</td>
                        <td className="px-2 py-1.5">{r.match_type}</td>
                        <td className="px-2 py-1.5 text-right font-semibold">{r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Sample records */}
            {affectedResults.map((r, i) => (
              r.samples?.length > 0 && (
                <details key={i} className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">
                    {r.collection}.{r.field} samples ({r.samples.length})
                  </summary>
                  <pre className="mt-1 p-2 bg-muted rounded text-[10px] overflow-x-auto max-h-40">
                    {JSON.stringify(r.samples, null, 2)}
                  </pre>
                </details>
              )
            ))}
          </div>
        )}

        {/* Apply result */}
        {applyResult && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              {applyResult.status === 'applied' ? (
                <><CheckCircle2 className="h-4 w-4 text-green-600" /> Merge applied: {applyResult.total_modified} records reassigned</>
              ) : (
                <><CheckCircle2 className="h-4 w-4 text-green-600" /> No changes needed (already merged)</>
              )}
            </div>

            {applyResult.retire_result && (
              <div className="text-xs text-muted-foreground">
                Entity record: {applyResult.retire_result.approach} ({applyResult.retire_result.reason})
              </div>
            )}

            {applyResult.post_merge_remaining > 0 && (
              <div className="flex items-center gap-2 text-sm text-amber-600">
                <AlertTriangle className="h-4 w-4" />
                Warning: {applyResult.post_merge_remaining} references still remain after merge
              </div>
            )}

            {applyResult.post_merge_remaining === 0 && (
              <div className="flex items-center gap-2 text-xs text-green-600">
                <CheckCircle2 className="h-3 w-3" /> Post-merge scan: zero remaining references
              </div>
            )}

            {applyResult.reassignments?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">
                  Reassignment details ({applyResult.reassignments.length} operations)
                </summary>
                <div className="border rounded mt-1 overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1 font-medium">Collection</th>
                        <th className="text-left px-2 py-1 font-medium">Field</th>
                        <th className="text-right px-2 py-1 font-medium">Matched</th>
                        <th className="text-right px-2 py-1 font-medium">Modified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applyResult.reassignments.map((r, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-2 py-1">{r.collection}</td>
                          <td className="px-2 py-1 font-mono">{r.field}</td>
                          <td className="px-2 py-1 text-right">{r.matched}</td>
                          <td className="px-2 py-1 text-right font-semibold">{r.modified}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
